/**
 * SVG flag rendering for the original-language tag.
 *
 * Flag emojis don't render reliably across platforms (notably Windows browsers
 * display flag emojis as plain two-letter country codes), so each flag is a
 * small inline SVG. The actual flag shapes are defined as plain data in
 * `@repo/shared/flag-data` (shared with the teaser web-component bundle); this
 * file is the `react-native-svg` renderer. `<LanguageFlag>` composes the
 * pieces: Swiss linguistic variants (any locale ending in `-CH`) are prefixed
 * with the Swiss cross to signal national cohesion; other languages show only
 * their own flag.
 */

import type { FlagShape } from '@repo/shared/flag-data';
import { getLanguageFlagShape, localeToLanguageFlag, SWISS_CROSS_FLAG } from '@repo/shared/flag-data';
import type { IsoLocale } from '@repo/shared/locale';
import { isSwissLocale } from '@repo/shared/locale';
import Svg, { Path, Rect } from 'react-native-svg';

function FlagSvg({ shape, size }: { shape: FlagShape; size: number }) {
    return (
        <Svg height={size * shape.aspect} viewBox={shape.viewBox} width={size}>
            {shape.primitives.map((primitive, index) =>
                primitive.kind === 'rect' ? (
                    <Rect
                        fill={primitive.fill}
                        height={primitive.height}
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        width={primitive.width}
                        x={primitive.x}
                        y={primitive.y}
                    />
                ) : (
                    <Path
                        d={primitive.d}
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        stroke={primitive.stroke}
                        strokeWidth={primitive.strokeWidth}
                    />
                ),
            )}
        </Svg>
    );
}

/**
 * Flag(s) for a language. Swiss linguistic variants are prefixed with the
 * Swiss cross to signal national cohesion; other languages show only their
 * own flag. Pass `showSwissPrefix` to force the Swiss prefix on regardless of
 * locale (used by silos that are Swiss but include non-`-CH` locales).
 */
export function LanguageFlag({ language, size = 12, showSwissPrefix }: { language: IsoLocale; size?: number; showSwissPrefix?: boolean }) {
    const subtag = localeToLanguageFlag(language);
    const shape = subtag ? getLanguageFlagShape(subtag) : null;
    const shouldShowSwissPrefix = showSwissPrefix ?? isSwissLocale(language);
    return (
        <>
            {shouldShowSwissPrefix && <FlagSvg shape={SWISS_CROSS_FLAG} size={size} />}
            {shape && <FlagSvg shape={shape} size={size} />}
        </>
    );
}
